import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import InteractiveStudyModeTailwind from '../Study/InteractiveStudyModeTailwind';
import StudyModeToggleTailwind from '../Study/StudyModeToggleTailwind';
import AdvancedVocabularyViewer from '../Vocabulary/AdvancedVocabularyViewer';
import StudyAssistant from '../StudyAssistant/StudyAssistant';
import { useAuth } from '../../contexts/AuthContext';

const CourseDetail = ({ course, syllabusContent, loading, error }) => {
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [studyMode, setStudyMode] = useState(false);
  const [editableContent, setEditableContent] = useState({});
  const [activeTab, setActiveTab] = useState('syllabus');
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [studyAssistantOpen, setStudyAssistantOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const courseStats = [
    { label: 'Duration', value: course.duration },
    { label: 'Level', value: course.difficulty },
    { label: 'Domain', value: course.domain },
    { label: 'Format', value: '12-Week Course' },
  ];

  const generateWeeklyContent = (content) => {
    if (!content) return [];
    
    const weeks = [];
    const weekRegex = /### Week (\d+): ([^\n]+)([\s\S]*?)(?=### Week \d+:|$)/g;
    let match;
    
    while ((match = weekRegex.exec(content)) !== null) {
      weeks.push({
        number: parseInt(match[1]),
        title: match[2].trim(),
        content: match[3].trim()
      });
    }
    
    return weeks.slice(0, 12);
  };

  const weeklyContent = generateWeeklyContent(syllabusContent);

  const handleContentUpdate = (lineIndex, newContent) => {
    setEditableContent(prev => ({
      ...prev,
      [lineIndex]: newContent
    }));
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading course content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fade-in">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Course</h3>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8">
      {/* Course Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8">
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0 text-5xl">
            {course.emoji}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {course.title}
            </h1>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {course.description}
            </p>

            {/* Course Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              {courseStats.map((stat, index) => (
                <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
              <button 
                onClick={() => setStudyAssistantOpen(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                🤖 AI Study Assistant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 sm:p-6 md:p-8">
          {/* Tab Navigation */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('syllabus')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'syllabus'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📖 Syllabus
              </button>
              <button
                onClick={() => setActiveTab('vocabulary')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'vocabulary'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📚 Vocabulary
              </button>
              <button
                onClick={() => setStudyAssistantOpen(true)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                🤖 AI Assistant
              </button>
            </div>
            
          </div>

          {/* Study Mode Toggle - only show on syllabus tab */}
          {activeTab === 'syllabus' && (
            <StudyModeToggleTailwind
              studyMode={studyMode}
              onToggle={setStudyMode}
              isAuthenticated={isAuthenticated}
            />
          )}
          
          {/* Tab Content */}
          {activeTab === 'syllabus' ? (
            <div>
              {!syllabusContent ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800">Syllabus content is being loaded. Please refresh the page if it doesn't appear shortly.</p>
                </div>
              ) : weeklyContent.length > 0 ? (
            <div className="space-y-4">
              {weeklyContent.map((week) => (
                <div key={week.number} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedWeek(expandedWeek === week.number ? null : week.number)}
                    className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Week {week.number}: {week.title}
                      </h3>
                    </div>
                    <svg 
                      className={`h-5 w-5 text-gray-400 transform transition-transform ${
                        expandedWeek === week.number ? 'rotate-180' : ''
                      }`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {expandedWeek === week.number && (
                    <div className="px-6 py-4 bg-white border-t border-gray-200">
                      <div className="syllabus-content prose max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => {
                              const content = children?.toString() || '';
                              
                              // Make paragraphs interactive in study mode
                              const shouldBeInteractive = isAuthenticated && studyMode && content.length > 30;
                              
                              if (shouldBeInteractive) {
                                const lineIndex = `${course.id}-${week.number}-p-${content.slice(0, 20)}`;
                                const currentContent = editableContent[lineIndex] || content;
                                
                                return (
                                  <div className="my-2">
                                    <InteractiveStudyModeTailwind
                                      content={currentContent}
                                      lineIndex={lineIndex}
                                      onContentUpdate={handleContentUpdate}
                                      courseColor={course.color}
                                    />
                                  </div>
                                );
                              }
                              
                              return <p className="mb-4">{children}</p>;
                            },
                            li: ({ children }) => {
                              const content = children?.toString() || '';
                              
                              // Check if this is a structural header
                              const isStructuralHeader = content.includes(':') && 
                                (content.includes('Objectives') || content.includes('Topics') || 
                                 content.includes('Assignments') || content.includes('Assessment') ||
                                 content.includes('Readings') || content.includes('Laboratory'));
                              
                              // Make list items interactive in study mode
                              const shouldBeInteractive = isAuthenticated && studyMode && 
                                !isStructuralHeader && content.length > 20;
                              
                              if (shouldBeInteractive) {
                                const lineIndex = `${course.id}-${week.number}-li-${content.slice(0, 20)}`;
                                const currentContent = editableContent[lineIndex] || content;
                                
                                return (
                                  <li className="mb-1">
                                    <InteractiveStudyModeTailwind
                                      content={currentContent}
                                      lineIndex={lineIndex}
                                      onContentUpdate={handleContentUpdate}
                                      courseColor={course.color}
                                    />
                                  </li>
                                );
                              }
                              
                              return <li className="mb-1">{children}</li>;
                            }
                          }}
                        >
                          {week.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="syllabus-content prose max-w-none">
              <ReactMarkdown
                components={{
                  p: ({ children }) => {
                    const content = children?.toString() || '';
                    
                    // Make paragraphs interactive in study mode
                    const shouldBeInteractive = isAuthenticated && studyMode && content.length > 30;
                    
                    if (shouldBeInteractive) {
                      const lineIndex = `${course.id}-syllabus-p-${content.slice(0, 20)}`;
                      const currentContent = editableContent[lineIndex] || content;
                      
                      return (
                        <div className="my-2">
                          <InteractiveStudyModeTailwind
                            content={currentContent}
                            lineIndex={lineIndex}
                            onContentUpdate={handleContentUpdate}
                            courseColor={course.color}
                          />
                        </div>
                      );
                    }
                    
                    return <p className="mb-4">{children}</p>;
                  },
                  li: ({ children }) => {
                    const content = children?.toString() || '';
                    
                    // Check if this is a structural header
                    const isStructuralHeader = content.includes(':') && 
                      (content.includes('Objectives') || content.includes('Topics') || 
                       content.includes('Assignments') || content.includes('Assessment') ||
                       content.includes('Readings') || content.includes('Laboratory'));
                    
                    // Make list items interactive in study mode
                    const shouldBeInteractive = isAuthenticated && studyMode && 
                      !isStructuralHeader && content.length > 20;
                    
                    if (shouldBeInteractive) {
                      const lineIndex = `${course.id}-syllabus-li-${content.slice(0, 20)}`;
                      const currentContent = editableContent[lineIndex] || content;
                      
                      return (
                        <li className="mb-1">
                          <InteractiveStudyModeTailwind
                            content={currentContent}
                            lineIndex={lineIndex}
                            onContentUpdate={handleContentUpdate}
                            courseColor={course.color}
                          />
                        </li>
                      );
                    }
                    
                    return <li className="mb-1">{children}</li>;
                  }
                }}
              >
                {syllabusContent}
              </ReactMarkdown>
            </div>
              )
              }
            </div>
          ) : (
            <AdvancedVocabularyViewer courseId={course.id} week={selectedWeek} />
          )}
        </div>
      </div>

      {/* AI Study Assistant Modal */}
      <StudyAssistant
        course={course}
        isOpen={studyAssistantOpen}
        onClose={() => setStudyAssistantOpen(false)}
      />
    </div>
  );
};

export default CourseDetail;